import { Signal, inject, signal, Service } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Router } from "@angular/router";
import { Observable, ReplaySubject } from "rxjs";

import { Configuration } from "@/shared-module/models";

@Service()
export class ConfigurationService {
  private http = inject(HttpClient);
  private router = inject(Router);

  private dataSubject = new ReplaySubject<Configuration>(1);
  configuration: Observable<Configuration> = this.dataSubject.asObservable();
  private readonly currentSignal = signal<Configuration | undefined>(undefined);
  fetched: boolean;
  configurationValue: Configuration;
  demoPopup: boolean;
  launchPopupAlreadyDisplayed: boolean;
  firstAccess: boolean;
  constructor() {
    this.configuration.subscribe((c) => {
      this.configurationValue = c;
      this.currentSignal.set(c);
    });
  }

  /**
   * The configuration as a signal (`undefined` until `api/configuration` has
   * answered). Like `config`, reading it triggers the first fetch, so a
   * component can read it in a field initialiser instead of subscribing to
   * `config` in its constructor — and OnPush templates that read it are
   * marked for check when the configuration (re)loads.
   */
  get current(): Signal<Configuration | undefined> {
    this.ensureFetched();
    return this.currentSignal.asReadonly();
  }

  private ensureFetched() {
    if (!this.fetched) {
      this.http
        .get<Configuration>(`api/configuration`)
        .subscribe((res) => this.dataSubject.next(res));
      this.fetched = true;
    }
  }

  setDemoPopup(value: boolean) {
    this.demoPopup = value;
  }

  displayDemoPopup() {
    return this.demoPopup;
  }

  setLaunchPopupAlreadyDisplayed(value: boolean) {
    this.launchPopupAlreadyDisplayed = value;
  }

  isLaunchPopupAlreadyDisplayed() {
    return this.launchPopupAlreadyDisplayed;
  }

  public get config(): any {
    this.ensureFetched();
    return this.configuration;
  }

  public refreshConfig(): any {
    return this.http
      .get<Configuration>(`api/configuration`)
      .subscribe((res) => this.dataSubject.next(res));
  }

  public redirectIfDisabled() {
    // Pages call this from their data subscription too, which can fire before
    // `api/configuration` has answered on a cold load; the config subscription
    // calls it again once the configuration is there, so just bail out here.
    if (this.configurationValue?.noRankingMode) {
      let url = this.configurationValue.menu.items[0].path;
      if (url == null)
        //case of "select teams when coming from faculty user interface"
        url = this.configurationValue.menu.items[1].path;
      this.router.navigateByUrl(url, { skipLocationChange: false });
    }
  }

  public setLanguage(language: string) {
    return this.http
      .post(`api/configuration/language/${language}`, null)
      .pipe((s) => {
        return s;
      });
  }

  public setHighContrast() {
    return this.http
      .post(`api/configuration/highcontrast/set`, null)
      .pipe((s) => {
        return s;
      });
  }

  public unsetHighContrast() {
    return this.http
      .post(`api/configuration/highcontrast/unset`, null)
      .pipe((s) => {
        return s;
      });
  }

  public setScreenReaderFriendly() {
    return this.http
      .post(`api/configuration/screenreaderfriendly/set`, null)
      .pipe((s) => {
        return s;
      });
  }

  public unsetScreenReaderFriendly() {
    return this.http
      .post(`api/configuration/screenreaderfriendly/unset`, null)
      .pipe((s) => {
        return s;
      });
  }

  getDemoParameters() {
    return this.http
      .get<string>(`api/configuration/demoparameters`)
      .pipe((s) => {
        return s;
      });
  }
}
